import { useEffect } from "react";
import { Button, Card, CardBody, Col, Row, Form } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { JobTypeService } from "./service";
import type { JobTypePayload } from "./interface";
import { ErrorToast, SuccessToast } from "../../shared/toasters/toasters";
import { useTranslation } from "react-i18next";
import FormErrorSummary from "../../shared/components/FormErrorSummary";

const schema = yup.object().shape({
  title: yup.string().required("formErrors.titleRequired"),
});

const JobTypeForm = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<JobTypePayload>({
    resolver: yupResolver(schema),
    defaultValues: { title: "" },
  });

  useEffect(() => {
    if (!isEdit) return;

    JobTypeService.getSingle(+id!)
      .then(reset)
      .catch(ErrorToast);
  }, [isEdit, id, reset]);

  const onSubmit = async (data: JobTypePayload) => {
    try {
      isEdit
        ? await JobTypeService.update(data, +id!)
        : await JobTypeService.create(data);

      SuccessToast(t(`jobType.${isEdit ? "successUpdate" : "successCreate"}`));
      navigate("/job-types");
    } catch (error) {
      ErrorToast(error);
    }
  };

  return (
    <Row>
      <Col sm={12}>
        <Card>
          <CardBody>
            <Form onSubmit={handleSubmit(onSubmit)}>
              <FormErrorSummary errors={errors} />
              <Row className="g-3">
                <Col sm={12} xl={6}>
                  <div className="mb-0">
                    <label className="form-label">{t("jobType.title")}</label>
                    <input
                      type="text"
                      className={`form-control ${errors.title ? "is-invalid" : ""}`}
                      {...register("title")}
                    />
                    {errors.title && (
                      <div className="invalid-feedback">
                        {t(errors.title.message as string)}
                      </div>
                    )}
                  </div>
                </Col>
                <Col sm={12}>
                  <Button type="submit" variant="primary">
                    {t(`jobType.${isEdit ? "update" : "create"}`)}
                  </Button>
                </Col>
              </Row>
            </Form>
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
};

export default JobTypeForm;